'use client';
import React, { useRef, useState } from 'react';
import { IconFileUpload, IconFile } from '@icons';
import { Chip, Button, ButtonClose } from '@ui';

const MAX_FILES = 10;
const MAX_MB = 10;

function fmtSize(bytes: number) {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

interface UploadingFile {
  file: File;
  progress: number;
  done: boolean;
  timer?: NodeJS.Timeout;
}

const FileUploader: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const [dragActive, setDragActive] = useState(false);

  function showToast(type: 'warning' | 'error', title: string, message: string) {
    // TODO: Integrate with your Toast system
    alert(`${title}: ${message}`);
  }

  function addFile(file: File) {
    const newFile: UploadingFile = { file, progress: 0, done: false };
    setFiles((prev) => {
      if (prev.length >= MAX_FILES) {
        showToast('warning', 'Limit', `Max ${MAX_FILES} files.`);
        return prev;
      }
      if (file.size > MAX_MB * 1024 * 1024) {
        showToast('error', 'Too large', `${file.name} exceeds ${MAX_MB} MB.`);
        return prev;
      }
      // Start mock progress
      const timer = setInterval(() => {
        setFiles((curr) =>
          curr.map((f) => {
            if (f.file === file && !f.done) {
              let next = f.progress + Math.random() * 18 + 8;
              if (next >= 100) {
                next = 100;
                clearInterval(f.timer!);
                return { ...f, progress: 100, done: true };
              }
              return { ...f, progress: next };
            }
            return f;
          })
        );
      }, 300);
      return [...prev, { ...newFile, timer }];
    });
  }

  function handleFiles(selected: FileList | File[]) {
    const arr = Array.from(selected);
    arr.forEach(addFile);
  }

  function removeFile(idx: number) {
    setFiles((prev) => {
      const f = prev[idx];
      if (f && f.timer) clearInterval(f.timer);
      return prev.filter((_, i) => i !== idx);
    });
  }

  function handleClearAll() {
    setFiles((prev) => {
      prev.forEach((f) => {
        if (f.timer) clearInterval(f.timer);
      });
      return [];
    });
  }

  return (
    <div
      className={`uploader${dragActive ? ' is-drag' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        handleFiles(e.dataTransfer.files);
      }}
    >
      <input
        type='file'
        multiple
        ref={inputRef}
        className='uploader-input'
        aria-label='Choose files'
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files!)}
      />
      <label
        className='card dropzone'
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
      >
        <div className='dropzone-inner'>
          <Button className='btn-icon' icon={<IconFileUpload />} />
          <b>Drag & drop files</b>
          <small className='hint'>
            or click to browse — up to {MAX_FILES} files, max {MAX_MB} MB each
          </small>
        </div>
      </label>
      {files.length > 0 && (
        <>
          <div className='row' style={{ justifyContent: 'space-between' }}>
            <small className='hint' id='uploadInfo'>
              {files.length} file{files.length !== 1 ? 's' : ''} • max {MAX_MB}
              MB each
            </small>
            <Button
              className='btn-sm btn-ghost'
              id='clearUploads'
              type='button'
              label='Clear all'
              onClick={handleClearAll}
            />
          </div>
          <div className='file-list' aria-live='polite'>
            {files.map((f, i) => (
              <div className='file-item' key={i}>
                <div className='file-meta'>
                  <div className='icon' aria-hidden='true'>
                    <IconFile />
                  </div>
                  <div>
                    <div className='file-name'>{f.file.name}</div>
                    <small className='file-size'>{fmtSize(f.file.size)}</small>
                    <div className='progress-line' aria-label='Upload progress'>
                      <span
                        style={
                          {
                            ['--val']: `${f.progress}%`,
                          } as React.CSSProperties & Record<string, string>
                        }
                      ></span>
                    </div>
                  </div>
                </div>
                <div className='file-actions'>
                  {f.done ? (
                    <>
                      <Chip label='Done' />
                      <ButtonClose
                        aria-label={`Remove ${f.file.name}`}
                        onClick={() => removeFile(i)}
                        className='btn-sm'
                      />
                    </>
                  ) : (
                    <Button
                      className='btn-ghost btn-sm'
                      aria-label='Cancel upload'
                      onClick={() => removeFile(i)}
                      label='Cancel'
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FileUploader;
